import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import {
	postMarque,
	postMarqueReset,
	deleteMarque,
	deleteMarqueReset,
	fetchVehModeles,
} from "../../../store/actions/autreActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"
import Loader from "../../basics/Loader"

const styles = {
	sectionTitle: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		marginTop: 20,
		marginBottom: 12,
	},
	modeleRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginBottom: 8,
	},
	delBtn: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 34,
		height: 34,
		flexShrink: 0,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.danger,
		cursor: "pointer",
		fontSize: 12,
	},
	empty: { color: COLORS.textLight, fontSize: 13, marginBottom: 8 },
}

// Édition d'une marque + ses modèles (modal V2, maître-détail). L'enregistrement
// envoie marque + liste complète des modèles à POST /api/modeles : le backend
// upsert les modèles présents et supprime ceux retirés (pruning). Supprimer la
// marque cascade ses modèles.
const MarqueModeleModalV2 = ({ marque, onClose }) => {
	const dispatch = useDispatch()
	const postState = useSelector((s) => s.autreInfo.postMarque)
	const deleteState = useSelector((s) => s.autreInfo.deleteMarque)
	const reduxModeles = useSelector((s) => s.autreInfo.modeles)
	const fetchModelesState = useSelector((s) => s.autreInfo.fetchModeles)
	const isCreate = !marque.id

	const [confirmDelete, setConfirmDelete] = useState(false)
	const [nom, setNom] = useState(marque.nom || "")
	const [modeles, setModeles] = useState([])
	const [seeded, setSeeded] = useState(!marque.id)
	const sawLoadingRef = useRef(false)

	// Chargement des modèles de la marque éditée.
	useEffect(() => {
		if (marque.id) dispatch(fetchVehModeles(marque.id))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Seed une seule fois, après que le fetch de CETTE marque soit terminé
	// (on attend d'avoir vu loading passer à true puis false → pas de données
	// périmées d'une marque précédemment ouverte).
	useEffect(() => {
		if (fetchModelesState.loading) sawLoadingRef.current = true
		if (!seeded && sawLoadingRef.current && !fetchModelesState.loading) {
			setModeles(
				(reduxModeles || []).map((m) => ({ id: m.id, nom: m.nom })),
			)
			setSeeded(true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchModelesState.loading])

	// Fermeture après une sauvegarde OU une suppression réussie.
	useEffect(() => {
		if (!postState.saved && !deleteState.deleted) return
		const t = setTimeout(() => {
			dispatch(postMarqueReset())
			dispatch(deleteMarqueReset())
			onClose()
		}, 800)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postState.saved, deleteState.deleted])

	useEffect(
		() => () => {
			dispatch(postMarqueReset())
			dispatch(deleteMarqueReset())
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	// Quitte le mode "confirmer" si la suppression échoue.
	useEffect(() => {
		if (deleteState.error) setConfirmDelete(false)
	}, [deleteState.error])

	const updateModele = (index, value) =>
		setModeles((prev) =>
			prev.map((m, i) => (i === index ? { ...m, nom: value } : m)),
		)
	const addModele = () =>
		setModeles((prev) => [...prev, { id: null, nom: "" }])
	const removeModele = (index) =>
		setModeles((prev) => prev.filter((_, i) => i !== index))

	const invalid = !nom.trim()
	const loadingModeles = !seeded && !!marque.id

	const save = () => {
		const payload = {
			marque: { id: marque.id || null, nom: nom.trim() },
			modeles: modeles
				.filter((m) => m.nom.trim() !== "")
				.map((m) => ({ id: m.id, nom: m.nom.trim() })),
		}
		dispatch(postMarque(JSON.stringify(payload)))
	}
	const remove = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		dispatch(deleteMarque(JSON.stringify({ id: marque.id })))
	}

	const busy =
		postState.saving ||
		postState.saved ||
		deleteState.deleting ||
		deleteState.deleted

	const buttons = []
	if (!isCreate) {
		buttons.push({
			title: deleteState.deleting ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : confirmDelete ? (
				"Confirmer ? (+ modèles)"
			) : (
				"Supprimer"
			),
			styleName: "danger",
			onClick: remove,
			disabled: busy,
		})
	}
	buttons.push({
		title: "Annuler",
		styleName: "secondary",
		onClick: onClose,
		disabled: busy,
	})
	buttons.push({
		title: postState.saving ? (
			<FontAwesomeIcon icon={faSync} spin />
		) : postState.saved ? (
			<FontAwesomeIcon icon={faCheck} />
		) : (
			"Enregistrer"
		),
		styleName: "primary",
		onClick: save,
		disabled: busy || invalid || loadingModeles,
	})

	return (
		<CustomModal
			title={isCreate ? "Nouvelle marque" : "Éditer la marque"}
			onClose={busy ? () => {} : onClose}
			showCloseButton={!busy}
			buttons={buttons}
		>
			<div style={gs.fieldGroup}>
				<div style={gs.fieldRow}>
					<label style={gs.label}>
						Nom<span style={{ color: COLORS.danger }}> *</span>
					</label>
					<input
						style={gs.input}
						value={nom}
						onChange={(e) => setNom(e.target.value)}
						autoFocus
					/>
				</div>
			</div>

			<div style={styles.sectionTitle}>Modèles</div>
			{loadingModeles ? (
				<Loader size={28} padding={24} message="Chargement…" />
			) : (
				<>
					{modeles.length === 0 && (
						<div style={styles.empty}>Aucun modèle.</div>
					)}
					{modeles.map((m, index) => (
						<div key={m.id ?? `new-${index}`} style={styles.modeleRow}>
							<input
								style={gs.input}
								value={m.nom}
								placeholder="Nom du modèle"
								onChange={(e) =>
									updateModele(index, e.target.value)
								}
							/>
							<button
								type="button"
								style={styles.delBtn}
								onClick={() => removeModele(index)}
								title="Retirer le modèle"
							>
								<FontAwesomeIcon icon={faTrash} />
							</button>
						</div>
					))}
					<button
						type="button"
						style={{ ...gs.addBtn, marginTop: 6 }}
						onClick={addModele}
					>
						<FontAwesomeIcon icon={faPlus} />
						Ajouter un modèle
					</button>
				</>
			)}
		</CustomModal>
	)
}

export default MarqueModeleModalV2
